import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronUpIcon, ChevronDownIcon, ChevronsUpDownIcon } from "lucide-react";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  total: number;
  offset: number;
  limit: number;
  onOffsetChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
}

const PAGE_SIZES = [25, 50, 100] as const;

export function DataTable<T>({
  data,
  columns,
  sorting,
  onSortingChange,
  total,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  onRowClick,
  rowKey,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
  });

  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  const btnCls =
    "rounded border border-line px-2.5 py-1 text-xs hover:bg-surface-2 focus:outline-none focus:ring-1 focus:ring-electric-blue disabled:opacity-30 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="rounded-lg border border-line-subtle bg-surface">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-line-subtle text-left text-fg-muted"
              >
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const ariaSortValue = sorted === "asc"
                    ? "ascending" as const
                    : sorted === "desc"
                      ? "descending" as const
                      : undefined;
                  return (
                    <th
                      key={header.id}
                      aria-sort={canSort ? (ariaSortValue ?? "none") : undefined}
                      className={`px-3 py-2.5 font-medium whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-fg transition-colors" : ""}`}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {canSort && (
                          <span className="inline-flex">
                            {sorted === "asc" ? (
                              <ChevronUpIcon className="h-3 w-3" />
                            ) : sorted === "desc" ? (
                              <ChevronDownIcon className="h-3 w-3" />
                            ) : (
                              <ChevronsUpDownIcon className="h-3 w-3 opacity-40" />
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={rowKey(row.original)}
                className={`border-b border-line-subtle text-fg ${onRowClick ? "cursor-pointer hover:bg-surface-2 transition-colors" : ""}`}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-3 py-2 font-mono whitespace-nowrap"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-line-subtle px-3 py-2.5 text-xs text-fg-muted">
        <span>
          {total > 0
            ? `Showing ${start}\u2013${end} of ${total.toLocaleString()}`
            : "No results"}
        </span>
        <div className="flex items-center gap-3">
          <select
            value={limit}
            onChange={(e) => {
              onLimitChange(Number(e.target.value));
              onOffsetChange(0);
            }}
            aria-label="Rows per page"
            className="rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:outline-none focus:ring-1 focus:ring-electric-blue"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s} / page
              </option>
            ))}
          </select>
          <button type="button" disabled={!canPrev} onClick={() => onOffsetChange(Math.max(0, offset - limit))} className={btnCls}>
            Prev
          </button>
          <button type="button" disabled={!canNext} onClick={() => onOffsetChange(offset + limit)} className={btnCls}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
